/**
 * Kaomoji (顔文字, "face letters"): Japanese-style emoticons built from punctuation,
 * kana, symbols and box-drawing characters, read upright — (◕‿◕) — unlike the sideways
 * Western :-) .
 *
 * The list is a hand-picked selection of long-established, widely shared faces, sorted by
 * the feeling they show. It leaves out anything crude, hostile or aimed at a group of
 * people, so it is safe to offer in a classroom. Nothing is generated.
 *
 * Kaomoji are ordinary text, so they paste anywhere. How they *look* depends on the
 * fonts on the reader's device: a character the font lacks appears as a box or a
 * fallback glyph, which can shift the face. The test suite checks that every character
 * is an assigned, printable Unicode character.
 */

import { randomBelow, type Uint32Source } from "@/lib/tool-kit/random-fun/rng";

export type Mood =
  | "happy"
  | "love"
  | "sad"
  | "annoyed"
  | "surprised"
  | "shy"
  | "confused"
  | "nervous"
  | "sleepy"
  | "shrug"
  | "animals"
  | "greeting"
  | "celebrate"
  | "cool"
  | "thinking"
  | "thanks";

export const MOODS: readonly Mood[] = [
  "happy",
  "love",
  "celebrate",
  "greeting",
  "thanks",
  "cool",
  "animals",
  "shy",
  "surprised",
  "confused",
  "thinking",
  "shrug",
  "nervous",
  "sleepy",
  "sad",
  "annoyed",
];

export const KAOMOJI: Record<Mood, readonly string[]> = {
  happy: [
    "(◕‿◕)",
    "(^‿^)",
    "(＾▽＾)",
    "(≧◡≦)",
    "(✿◠‿◠)",
    "٩(◕‿◕)۶",
    "(｡◕‿◕｡)",
    "(•‿•)",
    "ヽ(´▽`)/",
    "(´∀`)",
    "(*^▽^*)",
    "(^_^)",
    "(^o^)",
    "(☆▽☆)",
    "(≧▽≦)",
    "(*´▽`*)",
    "(๑˃ᴗ˂)ﻭ",
    "(=^‿^=)",
  ],
  love: [
    "(♥‿♥)",
    "(｡♥‿♥｡)",
    "(◍•ᴗ•◍)❤",
    "(づ｡◕‿‿◕｡)づ",
    "(⌒‿⌒)♡",
    "(*˘︶˘*).｡.:*♡",
    "(´,,•ω•,,)♡",
    "( ˘ ³˘)♥",
    "♡(｡- ω -)",
    "(っ˘з(˘⌣˘ )",
    "(｡・//ε//・｡)♡",
    "(♡°▽°♡)",
  ],
  celebrate: [
    "ヽ(>∀<☆)ノ",
    "\\(^o^)/",
    "＼(≧▽≦)／",
    "ヽ(♡‿♡)ノ",
    "٩(^‿^)۶",
    "(ﾉ◕ヮ◕)ﾉ*:･ﾟ✧",
    "☆*:.｡.o(≧▽≦)o.｡.:*☆",
    "★~(◠‿◕✿)",
    "(☆^ー^☆)",
    "o(≧∇≦o)(o≧∇≦)o",
    "＼(￣▽￣)／",
    "(ノ^ヮ^)ノ*:・ﾟ✧",
  ],
  greeting: [
    "(^_^)/",
    "(｡･ω･)ﾉﾞ",
    "ヾ(＾∇＾)",
    "(*^ω^)ノ",
    "( ´ ▽ ` )ﾉ",
    "(・ω・)ノ",
    "(^-^)ノ~~",
    "ヾ(^▽^*)))",
    "(＾ｰ＾)ﾉ",
    "ヾ(･ω･*)ﾉ",
  ],
  thanks: [
    "m(_ _)m",
    "(*_ _)人",
    "人(_ _*)",
    "(m´▽`)m",
    "(_ _)",
    "m(＿＿)m",
    "(〃´∀｀)ﾉ",
    "(*´∀`*)",
    "ありがとう(*^▽^*)",
    "(´▽`ʃ♡ƪ)",
  ],
  cool: [
    "(⌐■_■)",
    "( •_•)>⌐■-■",
    "(▀̿Ĺ̯▀̿ ̿)",
    "(￣ー￣)b",
    "(｀・ω・´)ゞ",
    "ヾ(⌐■_■)ノ♪",
    "(•̀ᴗ•́)و",
    "d(-_^)",
    "(๑•̀ㅂ•́)و✧",
    "(・`ω´・)",
  ],
  animals: [
    "ʕ•ᴥ•ʔ",
    "(=^･ω･^=)",
    "(=ↀωↀ=)✧",
    "U・ᴥ・U",
    "(◕ᴥ◕ʋ)",
    "(ᵔᴥᵔ)",
    "ʕ•́ᴥ•̀ʔ",
    "ฅ^•ﻌ•^ฅ",
    "(V●ᴥ●V)",
    "(=^-ω-^=)",
    "ଘ(੭ˊᵕˋ)੭*",
    "(◕‿◕)/ ฅ",
    "ᓚᘏᗢ",
    "(≚ᄌ≚)ƶZ",
    "ʕ ᵔᴥᵔ ʔ",
    "(๑•ᴗ•๑)",
  ],
  shy: [
    "(⁄ ⁄>⁄ ▽ ⁄<⁄ ⁄)",
    "(*/ω＼*)",
    "(〃▽〃)",
    "(/ω＼)",
    "(⁄⁄•⁄ω⁄•⁄⁄)",
    "(*^.^*)",
    "(//▽//)",
    "(＞﹏＜)",
    "(*ﾉωﾉ)",
    "(´｡• ω •｡`)",
    "(o^^o)",
    "(///▽///)",
  ],
  surprised: [
    "(⊙_⊙)",
    "(°o°)",
    "(O_O)",
    "(ﾟдﾟ)",
    "(◎_◎)",
    "(☉_☉)",
    "∑(ﾟДﾟ)",
    "Σ(°ロ°)",
    "(⊙︿⊙)",
    "(ʘ‿ʘ)",
    "(⊙.⊙)",
    "w(°ｏ°)w",
  ],
  confused: [
    "(・・?)",
    "(o_O)",
    "(⊙﹏⊙)",
    "(・_・?)",
    "(？_？)",
    "(⊙_☉)",
    "(°ー°〃)",
    "(´･_･`)",
    "(ーー;)",
    "(´・ω・`)?",
    "(˘･_･˘)",
    "(・・)?",
  ],
  thinking: [
    "(￢_￢)",
    "(-_-;)",
    "(๑•́ ₃ •̀๑)",
    "(¬‿¬)",
    "(・_・ヾ",
    "(－‸ლ)",
    "(・・;)",
    "(￣～￣;)",
    "(._.)",
    "( ˘･з･)",
    "(´-`).｡oO",
    "(⊙ˍ⊙)",
  ],
  shrug: [
    "¯\\_(ツ)_/¯",
    "¯\\(°_o)/¯",
    "┐(´д`)┌",
    "┐('～`；)┌",
    "┐(￣ヘ￣)┌",
    "¯\\_(⊙︿⊙)_/¯",
    "╮(╯_╰)╭",
    "ヽ(ー_ー )ノ",
    "┐(‘～`;)┌",
    "╮(￣▽￣)╭",
  ],
  nervous: [
    "(^_^;)",
    "(；一_一)",
    "(・_・;)",
    "(°_°;)",
    "(;・∀・)",
    "(~_~;)",
    "(；´Д｀)",
    "(´；ω；`)",
    "(；・∀・)",
    "(^◇^;)",
    "(¯―¯٥)",
    "(´-ω-`;)",
  ],
  sleepy: [
    "(－ω－) zzZ",
    "(-_-) zzz",
    "(￣o￣) zzZZzzZZ",
    "(∪｡∪)｡｡｡zzz",
    "(´-ω-`)",
    "(～﹃～)~zZ",
    "( ˘ω˘ )ｽﾔｧ…",
    "(－.－)...zzz",
    "(๑˘︶˘๑)",
    "(ᵕ—ᴗ—)",
    "(ᴗ˳ᴗ)zzZ",
    "(-ω-)/ zzZ",
  ],
  sad: [
    "(╥﹏╥)",
    "(T_T)",
    "(；＿；)",
    "(ಥ﹏ಥ)",
    "(︶︹︺)",
    "(っ˘̩╭╮˘̩)っ",
    "(ノ_<。)",
    "(πーπ)",
    "(〒﹏〒)",
    "(T▽T)",
    "(；д；)",
    "(´;ω;`)",
    "○| ̄|_",
    "(｡•́︿•̀｡)",
  ],
  annoyed: [
    "(¬_¬)",
    "(ಠ_ಠ)",
    "(｀ε´)",
    "(ò_ó)",
    "(>_<)",
    "(ー_ー)!!",
    "(一_一)",
    "(￣^￣)",
    "(╯°□°）╯︵ ┻━┻",
    "┬─┬ノ( º _ ºノ)",
    "(｀へ´)",
    "(눈_눈)",
    "(´-ε-`)",
    "(-_-メ)",
  ],
};

/**
 * Plain-English names for the faces people search for by name. Every other face is found
 * by its mood.
 */
export const FACE_NAMES: Readonly<Record<string, string>> = {
  "¯\\_(ツ)_/¯": "shrug",
  "(╯°□°）╯︵ ┻━┻": "table flip",
  "┬─┬ノ( º _ ºノ)": "put the table back",
  "ʕ•ᴥ•ʔ": "bear",
  "(=^･ω･^=)": "cat",
  "(=ↀωↀ=)✧": "cat",
  "(=^-ω-^=)": "cat",
  "ฅ^•ﻌ•^ฅ": "cat",
  ᓚᘏᗢ: "cat",
  "U・ᴥ・U": "dog",
  "(V●ᴥ●V)": "dog",
  "(◕ᴥ◕ʋ)": "dog",
  "(⌐■_■)": "sunglasses",
  "( •_•)>⌐■-■": "putting on sunglasses",
  "m(_ _)m": "bow",
  "(*_ _)人": "bow",
  "人(_ _*)": "bow",
  "(づ｡◕‿‿◕｡)づ": "hug",
  "(っ˘̩╭╮˘̩)っ": "hug",
  "○| ̄|_": "orz, despair",
};

export interface KaomojiEntry {
  face: string;
  mood: Mood;
  /** A name for faces people search for by name, otherwise empty. */
  name: string;
}

export const ALL_KAOMOJI: readonly KaomojiEntry[] = MOODS.flatMap((mood) =>
  KAOMOJI[mood].map((face) => ({ face, mood, name: FACE_NAMES[face] ?? "" }))
);

const WHITESPACE = /\s+/;

/** Splits a query into lower-case search words. */
export function queryWords(query: string): string[] {
  return query
    .toLowerCase()
    .split(WHITESPACE)
    .filter((word) => word.length > 0);
}

/**
 * The kaomoji that fit a mood filter and a search.
 *
 * `termsByMood` holds, for each mood, the words that find it ("happy", "smile", "joy"…),
 * so the search stays in the reader's language. Every query word must start one of a
 * kaomoji's words — its mood's terms or its own name — so "sleep" finds the sleepy
 * faces and "cat" finds the cats.
 */
export function findKaomoji(
  mood: Mood | "all",
  query: string,
  termsByMood: Readonly<Record<Mood, string>>
): KaomojiEntry[] {
  const words = queryWords(query);
  return ALL_KAOMOJI.filter((entry) => {
    if (mood !== "all" && entry.mood !== mood) {
      return false;
    }
    if (words.length === 0) {
      return true;
    }
    const haystack = queryWords(
      `${termsByMood[entry.mood].replaceAll("|", " ")} ${entry.name}`
    );
    return words.every((word) =>
      haystack.some((candidate) => candidate.startsWith(word))
    );
  });
}

export function randomKaomoji(
  entries: readonly KaomojiEntry[],
  source?: Uint32Source
): KaomojiEntry | null {
  const index = entries.length === 0 ? -1 : randomBelow(entries.length, source);
  return entries[index] ?? null;
}
